// Landing page

import { useEffect, useRef } from 'react';
import { Button, Typography, Space, Layout, Flex,  } from 'antd';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/images/nova-logo-blue-text.png';
import logo2 from '../../assets/images/nova-new-logo-white-text.png';
import { 
    TeamOutlined, DollarOutlined,
    SafetyCertificateOutlined, ArrowRightOutlined,
} from '@ant-design/icons'; 
import './LandingPage.scss';

const { Header, Content } = Layout;
const {  Text,Title, Paragraph } = Typography;

const LandingPage = () => {
    const navigate = useNavigate();
    const featuresRef = useRef<HTMLDivElement>(null);
    const featureCardsRef = useRef<HTMLDivElement[]>([]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.3
        };

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (entry.target.classList.contains('feature-card')) {
                        entry.target.classList.add('visible');
                    } else {
                        entry.target.classList.add('animate', 'fade-in-up');
                    }
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, observerOptions);

        // Observe feature cards
        featureCardsRef.current.forEach(card => {
            if (card) observer.observe(card);
        });

        // Observe section titles and other elements
        const animatedElements = document.querySelectorAll('.section-title, .hero-title, .hero-subtitle');
        animatedElements.forEach(element => {
            observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <Layout className="landing-layout">
            <Header className="landing-header">
                <Flex align="center" justify="space-between" className="nav-container">
                    <Flex className="logo-section">
                        <img
                            src={logo}
                            alt="Logo"
                            height={40}
                            onClick={() => navigate('/')}
                            style={{ cursor: 'pointer' }}
                        />
                    </Flex>

                    <Flex align="center" className="menu-section">
                        <Space className="auth-buttons">
                            <Button 
                                type="link" 
                                onClick={() => navigate('/login')}
                            >
                                Login
                            </Button>
                            {/* <Button 
                                type="primary"
                                onClick={() => navigate('/signup')}
                            >
                                Request a Demo
                            </Button> */}
                        </Space>
                    </Flex>
                </Flex>
            </Header>
            
            <Content>
                <div className="hero-section">
                    <div className="floating-objects">
                        <div className="object object-1"></div>
                        <div className="object object-2"></div>
                    </div>
                    <Flex vertical align="center" className="hero-content">
                        <Title level={1} className="hero-title animate">
                        Manage Donors & Fundraising Effortlessly!
                        </Title>
                        
                        <Paragraph className="hero-subtitle animate">
                            Nova CRM helps you track interactions, manage donations, and streamline 
                            donor management—all in one place.
                        </Paragraph>

                        <Space size="large" className="hero-cta">
                            <Button 
                                type="primary" 
                                size="large"
                                className="get-started-btn"
                                onClick={() => navigate('/login')}
                            >
                                Get Started
                                <ArrowRightOutlined />
                            </Button>
                            {/* <Button 
                                size="large"
                                className="demo-btn"
                                type="link"
                            >
                                Request a Demo
                            </Button> */}
                        </Space>    
                    </Flex>
                </div>

                <div className="features-section" ref={featuresRef}>
                    <div className="geometric-shapes">
                        <div className="shape"></div>
                        <div className="shape"></div>
                    </div>
                    <div className="floating-shape"></div>
                    <Flex vertical align="center" className="features-content">
                        <Title level={2} className="section-title animate">
                            Key Features
                        </Title>
                        
                        <div className="features-grid">
                            <div 
                                className="feature-card"
                                ref={el => el && featureCardsRef.current.push(el)}
                            >
                                <div className="icon-wrapper">
                                    <TeamOutlined className="feature-icon" />
                                </div>
                                <Title level={3}>Donor Management</Title>
                                <Paragraph>
                                    Efficiently organize and manage donor information, 
                                    track interactions, and maintain detailed profiles.
                                </Paragraph>
                            </div>

                            <div 
                                className="feature-card"
                                ref={el => el && featureCardsRef.current.push(el)}
                            >
                                <div className="icon-wrapper">
                                    <DollarOutlined className="feature-icon" />
                                </div>
                                <Title level={3}>Donation Tracking</Title>
                                <Paragraph>
                                    Track donations, generate reports, and analyze giving 
                                    patterns with our comprehensive tracking system.
                                </Paragraph>
                            </div>

                            {/* <div className="feature-card">
                                <div className="icon-wrapper">
                                    <BarChartOutlined className="feature-icon" />
                                </div>
                                <Title level={3}>Smart Analytics</Title>
                                <Paragraph>
                                    Make data-driven decisions with powerful analytics 
                                    and insightful dashboards.
                                </Paragraph>
                            </div> */}

                            <div 
                                className="feature-card"
                                ref={el => el && featureCardsRef.current.push(el)}
                            >
                                <div className="icon-wrapper">
                                    <SafetyCertificateOutlined className="feature-icon" />
                                </div>
                                <Title level={3}>Access Control</Title>
                                <Paragraph>
                                    Secure your data with role-based access control 
                                    and comprehensive permission management.
                                </Paragraph>
                            </div>
                        </div>
                    </Flex>
                </div>

                {/* Testimonials Section */}
                {/* <div className="testimonials-section">
                    <Flex vertical align="center" className="testimonials-content">
                        <Title level={2} className="section-title">
                            What Our Users Say
                        </Title>
                        
                        <div className="testimonials-grid">
                            <div className="testimonial-card">
                                <div className="rating">
                                    {[...Array(5)].map((_, i) => (
                                        <StarFilled key={i} />
                                    ))}
                                </div>
                                <Paragraph className="quote">
                                    "Nova CRM has transformed how we manage our donor relationships. 
                                    The interface is intuitive and the features are exactly what we needed."
                                </Paragraph>
                                <div className="author">
                                    <Title level={4}>Sarah Johnson</Title>
                                    <Text>Development Director, Hope Foundation</Text>
                                </div>
                            </div>

                            <div className="testimonial-card">
                                <div className="rating">
                                    {[...Array(5)].map((_, i) => (
                                        <StarFilled key={i} />
                                    ))}
                                </div>
                                <Paragraph className="quote">
                                    "The donation tracking features have helped us increase our 
                                    fundraising efficiency by 40%. Excellent support team too!"
                                </Paragraph>
                                <div className="author">
                                    <Title level={4}>Michael Chen</Title>
                                    <Text>Executive Director, Global Charity</Text>
                                </div>
                            </div>

                            <div className="testimonial-card">
                                <div className="rating">
                                    {[...Array(5)].map((_, i) => (
                                        <StarFilled key={i} />
                                    ))}
                                </div>
                                <Paragraph className="quote">
                                    "Best investment we've made for our organization. The people 
                                    management tools are exceptional."
                                </Paragraph>
                                <div className="author">
                                    <Title level={4}>Emily Rodriguez</Title>
                                    <Text>Operations Manager, Care Network</Text>
                                </div>
                            </div>
                        </div>
                    </Flex>
                </div> */}

                {/* CTA Section */}
                <div className="cta-section">
                    <Flex vertical align="center" className="cta-content">
                        <Title level={2}>Ready to Get Started?</Title>
                        <Title level={3} className="cta-subtitle">Transform Your Donor Management Today</Title>
                        <Paragraph>
                            Join thousands of organizations already using Nova CRM to streamline their operations
                            and boost their fundraising efforts.
                        </Paragraph>
                       
                        {/* <Space size="large" className="cta-buttons">
                            <Button type="primary" size="large" className="start-btn">
                                Start for Free
                                <ArrowRightOutlined />
                            </Button>
                            <Button size="large" className="demo-btn">
                                Schedule a Demo
                            </Button>
                        </Space> */}
                    </Flex>
                </div>

                {/* Footer */}
                <div className="footer-section">
                    <Flex className="footer-content">
                        <div className="footer-grid">
                            <div className="footer-column">
                                <img src={logo2} alt="Logo" height={100} />
                                <Paragraph>
                                    Empowering organizations with smart donor management solutions.
                                </Paragraph>
                                {/* <Space className="social-links">
                                    <Button type="link" icon={<LinkedinFilled />} />
                                    <Button type="link" icon={<TwitterOutlined />} />
                                    <Button type="link" icon={<FacebookFilled />} />
                                    <Button type="link" icon={<InstagramFilled />} />
                                </Space> */}
                            </div>

                            {/* <div className="footer-column">
                                <Title level={4}>Product</Title>
                                <Button type="link">Features</Button>
                                <Button type="link">Pricing</Button>
                                <Button type="link">Security</Button>
                                <Button type="link">Updates</Button>
                            </div>

                            <div className="footer-column">
                                <Title level={4}>Company</Title>
                                <Button type="link">About Us</Button>
                                <Button type="link">Careers</Button>
                                <Button type="link">Contact Us</Button>
                                <Button type="link">Blog</Button>
                            </div> */}

                            <div className="footer-column">
                                <Button 
                                    type="link" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/contact')}
                                >
                                    Contact Support Team
                                </Button>
                            </div>
                        </div>
                    </Flex>
                    <div className="footer-bottom">
                        <Text>© {new Date().getFullYear()} Nova CRM. All rights reserved.</Text>
                    </div>
                </div>
            </Content>
        </Layout>
    );
};

export default LandingPage;